import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  CheckCircle2,
  ArrowLeft,
  Loader2,
  Sparkles,
  Download,
  Star,
  X
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TarifsSynthesePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [subscribing, setSubscribing] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (searchParams.get("canceled")) {
      toast.error("Paiement annulé");
    }
    if (searchParams.get("success")) {
      toast.success("Abonnement activé avec succès !");
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/api/synthese/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentSubscription(response.data);
      } catch (err) {
        console.error("Error fetching subscription:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubscription();
  }, []);

  const plans = [
    {
      id: "synthese_essentiel",
      name: "Essentiel",
      price: 9,
      period: "mois",
      description: "Pour la PME qui répond occasionnellement",
      features: [
        { text: "10 synthèses/mois", included: true },
        { text: "Niveaux Courte + Standard", included: true },
        { text: "Historique 7 jours", included: true },
        { text: "Support par email", included: true },
        { text: "Niveau Détaillée", included: false },
        { text: "Export PDF", included: false },
        { text: "Instructions sauvegardées", included: false },
        { text: "Onboarding téléphonique", included: false }
      ],
      highlighted: false
    },
    {
      id: "synthese_pro",
      name: "Pro",
      price: 19,
      period: "mois",
      description: "Pour la PME régulière",
      badge: "Recommandé",
      features: [
        { text: "Synthèses illimitées", included: true },
        { text: "Tous les niveaux (Courte, Standard, Détaillée)", included: true },
        { text: "Historique 90 jours", included: true },
        { text: "Support prioritaire", included: true },
        { text: "Export PDF inclus", included: true, highlight: true },
        { text: "Instructions sauvegardées", included: true },
        { text: "Onboarding téléphonique", included: true }
      ],
      highlighted: true
    }
  ];

  const handleSubscribe = async (planId) => {
    setSubscribing(planId);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/synthese/checkout/${planId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("URL de paiement non reçue");
      }
    } catch (err) {
      console.error("Subscription error:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de la souscription");
      setSubscribing(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/synthese-marches")}
          className="mb-6 text-slate-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux synthèses
        </Button>

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <Badge className="mb-4 bg-violet-100 text-violet-700">
            <Sparkles className="w-3 h-3 mr-1" />
            Synthèse IA
          </Badge>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-3">
            Choisissez votre plan <span className="italic text-violet-600">Synthèse IA</span>
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto text-sm">
            Analysez vos RC, CCAP et CCTP en quelques secondes. 
            L'export PDF vous permet de partager vos synthèses avec votre direction.
          </p>
        </motion.div>

        {/* Current subscription status */}
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : currentSubscription?.has_subscription && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">
                  Abonnement {currentSubscription.plan_name} actif
                </p>
                <p className="text-sm text-green-600">
                  {currentSubscription.usage_this_month} / {currentSubscription.limit === 999 ? "∞" : currentSubscription.limit} synthèses ce mois
                  {currentSubscription.pdf_export_enabled && " • Export PDF activé"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`h-full relative overflow-hidden ${
                plan.highlighted 
                  ? 'border-2 border-violet-500 shadow-lg' 
                  : 'border-slate-200'
              }`}>
                {plan.badge && (
                  <div className="absolute top-0 right-0">
                    <Badge className="rounded-none rounded-bl-lg bg-violet-600 text-white px-3 py-1 text-xs">
                      <Star className="w-3 h-3 mr-1" />
                      {plan.badge}
                    </Badge>
                  </div>
                )}
                
                <CardContent className="p-5">
                  <h3 className="font-display text-xl font-bold text-slate-900 mb-1">
                    {plan.name}
                  </h3>
                  <p className="text-slate-500 text-xs mb-3">{plan.description}</p>
                  
                  <div className="mb-5">
                    <span className="text-3xl font-display font-bold text-slate-900">
                      {plan.price}€
                    </span>
                    <span className="text-slate-500 text-sm">/{plan.period}</span>
                  </div>

                  <ul className="space-y-2 mb-5">
                    {plan.features.map((feature, i) => (
                      <li key={`item-${i}`} className={`flex items-start gap-2 text-sm ${
                        feature.included ? 'text-slate-700' : 'text-slate-400'
                      }`}>
                        {feature.included ? (
                          <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                            feature.highlight ? 'text-violet-600' : 'text-green-500'
                          }`} />
                        ) : (
                          <X className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-300" />
                        )}
                        <span className={feature.highlight ? 'font-medium text-violet-700' : ''}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribing === plan.id || currentSubscription?.plan_id === plan.id}
                    className={`w-full ${
                      plan.highlighted
                        ? 'bg-violet-600 hover:bg-violet-700 text-white'
                        : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                  >
                    {subscribing === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Redirection...
                      </>
                    ) : currentSubscription?.plan_id === plan.id ? (
                      "Plan actuel"
                    ) : (
                      `Choisir ${plan.name}`
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Feature highlight - Export PDF */}
        <Card className="border-violet-200 bg-violet-50/50 mb-8">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                <Download className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1 text-sm">
                  L'export PDF fait la différence
                </h3>
                <p className="text-slate-600 text-xs">
                  Les chargés d'affaires ont besoin de transmettre leurs synthèses à leur direction
                  ou d'archiver leurs analyses. À seulement <strong>10€ d'écart</strong>, le passage au plan
                  Pro s'impose naturellement.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h4 className="font-medium text-slate-900 mb-1 text-sm">
                Puis-je changer de plan ?
              </h4>
              <p className="text-xs text-slate-600">
                Oui, vous pouvez passer de Essentiel à Pro à tout moment.
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h4 className="font-medium text-slate-900 mb-1 text-sm">
                Limite atteinte ?
              </h4>
              <p className="text-xs text-slate-600">
                Avec Essentiel, passez au plan Pro pour continuer vos analyses.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
